import { useEffect, useState } from "react";
import { Calculator, Target, GraduationCap, ClipboardCheck, Inbox } from "lucide-react";

type Lead = {
  name: string;
  company: string;
  result: string;
  tool: string;
  icon: typeof Calculator;
  badgeClass: string;
};

const LEADS: Lead[] = [
  {
    name: "Morgan Ellis",
    company: "Pharmacy CBS",
    result: "Requested a practice valuation",
    tool: "Pharmacy Valuation Tool",
    icon: Calculator,
    badgeClass: "bg-cyan-400/15 text-cyan-300 border-cyan-400/30",
  },
  {
    name: "Jordan Blake",
    company: "Boom Marketing",
    result: "Wants to see the full framework",
    tool: "Campaign Framework Builder",
    icon: Target,
    badgeClass: "bg-violet-400/15 text-violet-300 border-violet-400/30",
  },
  {
    name: "Taylor Morgan",
    company: "ACRE Workplace",
    result: "Finished the classroom assessment",
    tool: "Learning Environment Assessment",
    icon: GraduationCap,
    badgeClass: "bg-sky-400/15 text-sky-300 border-sky-400/30",
  },
  {
    name: "Alex Parker",
    company: "GatorGuard Concrete Coatings",
    result: "Saved a personalized floor preview",
    tool: "Floor Visualizer",
    icon: ClipboardCheck,
    badgeClass: "bg-teal-400/15 text-teal-300 border-teal-400/30",
  },
  {
    name: "Jamie Reed",
    company: "Pharmacy CBS",
    result: "Requested a practice valuation",
    tool: "Pharmacy Valuation Tool",
    icon: Calculator,
    badgeClass: "bg-cyan-400/15 text-cyan-300 border-cyan-400/30",
  },
  {
    name: "Riley Cooper",
    company: "Boom Marketing",
    result: "Wants to see the full framework",
    tool: "Campaign Framework Builder",
    icon: Target,
    badgeClass: "bg-violet-400/15 text-violet-300 border-violet-400/30",
  },
  {
    name: "Cameron Brooks",
    company: "ACRE Workplace",
    result: "Finished the classroom assessment",
    tool: "Learning Environment Assessment",
    icon: GraduationCap,
    badgeClass: "bg-sky-400/15 text-sky-300 border-sky-400/30",
  },
  {
    name: "Avery Collins",
    company: "GatorGuard Concrete Coatings",
    result: "Compared three coating styles",
    tool: "Floor Visualizer",
    icon: ClipboardCheck,
    badgeClass: "bg-teal-400/15 text-teal-300 border-teal-400/30",
  },
];

const VISIBLE_ROWS = 5;
const BASE_COUNT = 128;
const TICK_MS = 2500;

export function LeadsInbox() {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => clearInterval(interval);
  }, []);

  // Newest lead first; the window slides forward through LEADS as tick grows.
  const rows = Array.from({ length: VISIBLE_ROWS }, (_, i) => {
    const leadIndex = (tick - i + LEADS.length * 1000) % LEADS.length;
    return { lead: LEADS[leadIndex], ageMinutes: i * 2, isNew: i === 0 };
  });

  return (
    <div className="flex h-full min-w-0 w-full items-center justify-center">
      <div className="w-full min-w-0 max-w-md overflow-hidden rounded-xl border border-white/10 bg-white/[0.04] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)] backdrop-blur-md sm:rounded-2xl">
        {/* Header */}
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-white/10 px-4 py-3.5 sm:px-5">
          <div className="flex items-center gap-2.5">
            <Inbox className="h-4 w-4 text-primary" aria-hidden="true" />
            <span className="text-sm font-semibold text-white">New leads</span>
            <span
              className="relative flex h-2 w-2 motion-reduce:hidden"
              aria-hidden="true"
            >
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
            </span>
          </div>
          <span className="text-xs tabular-nums text-muted-foreground">
            {BASE_COUNT + tick} this month
          </span>
        </div>

        {/* Lead rows */}
        <ul className="divide-y divide-white/[0.06]">
          {rows.map(({ lead, ageMinutes, isNew }, i) => {
            const Icon = lead.icon;
            return (
              <li
                key={`${lead.name}-${tick - i}`}
                 className={`flex min-w-0 items-center gap-2.5 px-4 py-3 sm:gap-3 sm:px-5 ${
                  isNew
                    ? "animate-[lead-in_0.5s_ease-out] motion-reduce:animate-none bg-primary/[0.06]"
                    : ""
                }`}
              >
                <span
                  className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${lead.badgeClass}`}
                  aria-hidden="true"
                >
                  <Icon className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-white">
                    {lead.name}
                    <span className="text-muted-foreground font-normal"> · {lead.company}</span>
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {lead.result}
                  </span>
                </span>
                <span className="shrink-0 text-right">
                  <span className={`block text-[11px] font-medium ${isNew ? "text-primary" : "text-muted-foreground"}`}>
                    {isNew ? "just now" : `${ageMinutes}m ago`}
                  </span>
                  <span className="hidden sm:block text-[11px] text-muted-foreground/70">{lead.tool}</span>
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
